import {getComboMarkdownEditor} from '../../../js/features/comp/ComboMarkdownEditor.ts';

const activePollIntervalMs = 5000;
// after a sidebar update (assignee/reviewer change) the session row is created asynchronously,
// so poll a few extra times before falling back to the idle interval
const rapidPollIntervalMs = 1500;
// keep a slow poll when nothing runs, a rerun started from the Actions page must show up here too
const idlePollIntervalMs = 30000;
const requestCheckDurationMs = 20000;

const activeSessionPhases = new Set(['queued', 'working', 'applying']);

// AgentRequest is a request whose session the worker queue creates asynchronously: a mentioned
// comment, or the retry of a failed session.
type AgentRequest = {
  commentID?: string,
  // findingCards is the number of run cards a finding page showed when a person asked an agent on it
  findingCards?: number,
  retrySessionID?: string,
  agentUser?: string,
  retryCreated?: number,
  expires: number,
};

function isValidRequest(request: AgentRequest): boolean {
  if (!request.expires || Date.now() >= request.expires) return false;
  if (request.findingCards !== undefined) return Number.isSafeInteger(request.findingCards);
  if (request.retrySessionID) return typeof request.agentUser === 'string' && Number.isSafeInteger(request.retryCreated);
  return Number.isSafeInteger(Number(request.commentID));
}

// isRequestAnswered reports whether the timeline shows the session or the deferred request that
// answers the request.
function isRequestAnswered(request: AgentRequest): boolean {
  // a request on a finding is answered by a run card that was not there before
  if (request.findingCards !== undefined) return document.querySelectorAll('.ee-agent-session[data-session-id]').length > request.findingCards;
  if (!request.retrySessionID) {
    return Boolean(document.querySelector(`.ee-agent-session[data-trigger-comment-id="${CSS.escape(request.commentID!)}"]`));
  }
  // the new session keeps the trigger comment of the retried one, so match the agent instead
  for (const card of document.querySelectorAll<HTMLElement>(`.ee-agent-session[data-agent-user="${CSS.escape(request.agentUser!)}"]`)) {
    if (card.getAttribute('data-session-id') === request.retrySessionID) continue;
    if (Number(card.getAttribute('data-session-created') || '0') > request.retryCreated!) return true;
  }
  return false;
}

// timelineList is the element the timeline entries are children of: the one that holds the end
// marker, which is the comment list of an issue and the timeline of a finding.
function timelineList(): HTMLElement | null {
  return document.querySelector<HTMLElement>('#timeline-comments-end')?.parentElement ??
    document.querySelector<HTMLElement>('.issue-content-left .comment-list') ?? document.querySelector<HTMLElement>('.comment-list');
}

function timelineItemUnix(el: Element): number {
  const timeEl = el.querySelector<HTMLElement>('relative-time[datetime]');
  if (!timeEl) return 0;
  const parsed = Date.parse(timeEl.getAttribute('datetime')!);
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed / 1000);
}

// isTimelineAnchor reports whether a comment list child is an insertion anchor. A review wraps its
// entries in a timeline-item-group, so both wrappers count, otherwise a session entry would land
// after a review that is newer than it.
function isTimelineAnchor(el: Element): boolean {
  if (el.classList.contains('form')) return false;
  return el.classList.contains('timeline-item') || el.classList.contains('timeline-item-group');
}

// placeSessionCard inserts a session entry at its chronological position, agent runs must not pile
// up at the bottom of the timeline. The entry is ordered by the time it shows: a finished session
// uses its finish time, a running one its creation time.
function placeSessionCard(card: HTMLElement): void {
  const list = timelineList();
  if (!list) return;
  const cardUnix = timelineItemUnix(card) || Number(card.getAttribute('data-session-created') || '0');
  const endMarker = document.querySelector<HTMLElement>('#timeline-comments-end');
  for (const item of list.children) {
    if (item === card || item === endMarker) continue;
    if (!isTimelineAnchor(item)) continue;
    const itemUnix = timelineItemUnix(item);
    if (itemUnix && cardUnix && itemUnix > cardUnix) {
      list.insertBefore(card, item);
      return;
    }
  }
  if (endMarker?.parentElement === list) {
    list.insertBefore(card, endMarker);
  } else {
    list.append(card);
  }
}

type ReviewerBadge = {phase: string, label: string};

function syncReviewerBadges(cards: HTMLElement[]): void {
  const badgeByUser = new Map<string, ReviewerBadge>();
  for (const card of cards) {
    const user = card.getAttribute('data-agent-user');
    if (!user) continue;
    // the newest session of an agent wins, the cards are ordered oldest first
    badgeByUser.set(user.toLowerCase(), {
      phase: card.getAttribute('data-session-phase') || '',
      label: card.getAttribute('data-phase-label') || '',
    });
  }
  const reviewerList = document.querySelector<HTMLElement>('.issue-content-right .issue-sidebar-combo');
  if (!reviewerList) return;
  for (const link of reviewerList.querySelectorAll<HTMLAnchorElement>('.ui.list .item a[href]')) {
    const user = link.getAttribute('href')!.split('/').filter(Boolean).pop()?.toLowerCase();
    if (!user) continue;
    const info = badgeByUser.get(user);
    const badge = link.parentElement?.querySelector<HTMLElement>('.ee-agent-reviewer-badge');
    if (!info) {
      badge?.remove();
    } else if (!badge || badge.textContent !== info.label || !badge.classList.contains(`is-${info.phase}`)) {
      const updated = document.createElement('span');
      updated.className = `ee-agent-reviewer-badge is-${info.phase}`;
      updated.textContent = info.label;
      if (badge) badge.replaceWith(updated);
      else link.after(updated);
    }
  }
}

function collectActiveSessionIDs(): Set<string> {
  const active = new Set<string>();
  for (const card of document.querySelectorAll<HTMLElement>('.ee-agent-session[data-session-id]')) {
    const phase = card.getAttribute('data-session-phase') || '';
    if (activeSessionPhases.has(phase)) {
      active.add(card.getAttribute('data-session-id')!);
    }
  }
  return active;
}

function preserveTimelinePosition(update: () => void): void {
  const list = timelineList();
  const firstVisible = Array.from(list?.children ?? []).find((item) => item.getBoundingClientRect().bottom > 0 && item.getBoundingClientRect().top < window.innerHeight);
  // A session card can move past new comments; following it would hide those comments.
  const anchor = firstVisible?.classList.contains('ee-agent-session') ? null : firstVisible;
  const anchorID = anchor?.id;
  const before = anchor?.getBoundingClientRect().top;
  update();
  const current = anchorID ? document.querySelector<HTMLElement>(`#${CSS.escape(anchorID)}`) : anchor;
  if (current?.isConnected && before !== undefined) window.scrollBy(0, current.getBoundingClientRect().top - before);
}

// The page response supplies the community-rendered comment/review HTML without modifying its templates.
async function refreshTimeline(addedEntries: WeakSet<Node>): Promise<void> {
  const response = await fetch(window.location.href, {headers: {'x-requested-with': 'XMLHttpRequest'}}); // eslint-disable-line no-restricted-globals
  if (!response.ok) throw new Error('Could not load the issue timeline');
  const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
  const source = parsed.querySelector<HTMLElement>('#timeline-comments-end')?.parentElement;
  const target = timelineList();
  const end = target?.querySelector<HTMLElement>('#timeline-comments-end');
  if (!source?.querySelector('#timeline-comments-end') || !target || !end) throw new Error('Issue timeline is unavailable');

  preserveTimelinePosition(() => {
    const entries = Array.from(source.children);
    const existing = new Map(Array.from(target.children).filter((entry) => entry.id).map((entry) => [entry.id, entry]));
    for (let index = 0; index < entries.length; index++) {
      const entry = entries[index];
      if (entry.id === 'timeline-comments-end') break;
      // Only insert server-identified entries; never overwrite editors, reactions, or live controls.
      if (!entry.id || existing.has(entry.id)) continue;
      const next = entries.slice(index + 1).find((candidate) => existing.has(candidate.id));
      addedEntries.add(entry);
      target.insertBefore(entry, next ? existing.get(next.id)! : end);
      existing.set(entry.id, entry);
    }
    for (const card of target.querySelectorAll<HTMLElement>('.ee-agent-session[data-session-phase="completed"]')) placeSessionCard(card);
  });
}

function sameCardMarkup(existing: HTMLElement, fresh: HTMLElement): boolean {
  if (existing.outerHTML === fresh.outerHTML) return true;
  const comparable = existing.cloneNode(true) as HTMLElement;
  const freshTimes = fresh.querySelectorAll('relative-time');
  const times = comparable.querySelectorAll('relative-time');
  if (times.length !== freshTimes.length) return false;
  for (let index = 0; index < times.length; index++) {
    // The live time and its tooltip mutate these attributes, but DOMParser leaves them untouched.
    for (const attribute of ['data-tooltip-content', 'aria-label', 'aria-controls']) {
      const value = freshTimes[index].getAttribute(attribute);
      if (value === null) times[index].removeAttribute(attribute);
      else times[index].setAttribute(attribute, value);
    }
  }
  return comparable.outerHTML === fresh.outerHTML;
}

// syncCards replaces, inserts and removes the timeline entries identified by one attribute, so the
// session cards and the pending request entries share the same update logic.
function syncCards(fresh: HTMLElement, attribute: string): HTMLElement[] {
  const cards: HTMLElement[] = [];
  const seen = new Set<string>();
  for (const freshCard of fresh.querySelectorAll<HTMLElement>(`.ee-agent-session[${CSS.escape(attribute)}]`)) {
    const key = freshCard.getAttribute(attribute)!;
    seen.add(key);
    const existing = document.querySelector<HTMLElement>(`.ee-agent-session[${CSS.escape(attribute)}="${CSS.escape(key)}"]`);
    if (existing && sameCardMarkup(existing, freshCard)) {
      cards.push(existing);
      continue;
    }
    if (existing) {
      existing.replaceWith(freshCard);
    }
    // place both a new and an updated entry: a session that just finished shows its finish time now
    // and has to move down to that position.
    placeSessionCard(freshCard);
    cards.push(freshCard);
  }
  for (const stale of document.querySelectorAll<HTMLElement>(`.ee-agent-session[${CSS.escape(attribute)}]`)) {
    if (!seen.has(stale.getAttribute(attribute)!)) stale.remove();
  }
  return cards;
}

async function loadAgentSessions(container: HTMLElement, refreshedSessions: Set<string>, addedEntries: WeakSet<Node>, isFirstLoad: boolean): Promise<boolean> {
  const url = container.getAttribute('data-agent-sessions-url');
  if (!url) return false;
  const response = await fetch(url, {headers: {'x-requested-with': 'XMLHttpRequest'}}); // eslint-disable-line no-restricted-globals
  if (!response.ok) return false;
  const activeBefore = collectActiveSessionIDs();
  const awaitingNewSession = !isFirstLoad || container.getAttribute('data-agent-awaiting-start') === 'true';
  const knownBefore = new Set(Array.from(document.querySelectorAll('.ee-agent-session[data-session-id]'), (card) => card.getAttribute('data-session-id')!));
  const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
  const fresh = parsed.querySelector<HTMLElement>('#ee-agent-sessions');
  if (!fresh) return false;
  container.setAttribute('data-agent-sessions-active', fresh.getAttribute('data-agent-sessions-active') || 'false');
  container.setAttribute('data-agent-awaiting-start', fresh.getAttribute('data-agent-awaiting-start') === 'true' ? 'true' : 'false');
  container.setAttribute('data-agent-awaiting-mention', fresh.getAttribute('data-agent-awaiting-mention') === 'true' ? 'true' : 'false');
  container.setAttribute('data-assign-checking-label', fresh.getAttribute('data-assign-checking-label') || '');

  const completed = fresh.querySelectorAll<HTMLElement>('.ee-agent-session[data-session-phase="completed"][data-session-id]');
  preserveTimelinePosition(() => {
    const cards = syncCards(fresh, 'data-session-id');
    // requests that wait for the running session disappear as soon as the deferred run starts.
    syncCards(fresh, 'data-pending-id');
    syncReviewerBadges(cards);
  });
  for (const card of completed) {
    const id = card.getAttribute('data-session-id')!;
    // a session that started and finished between two polls was never seen active, but its comments are new
    const startedAfterAssign = awaitingNewSession && !knownBefore.has(id);
    if (!(activeBefore.has(id) || startedAfterAssign) || refreshedSessions.has(id)) continue;
    refreshedSessions.add(id);
    try {
      await refreshTimeline(addedEntries);
    } catch {
      // The timeline stays as is; a failed refresh is not worth alarming the user.
    }
  }
  return true;
}

// initAgentSessions renders the agent session entries of an issue/pull page in chronological order
// and keeps polling while at least one session is still queued or running.
export function initAgentSessions(): void {
  const container = document.querySelector<HTMLElement>('#ee-agent-sessions');
  if (!container) return;

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let rapidPolls = 0;
  let hasLoaded = false;
  const refreshedSessions = new Set<string>();
  const addedEntries = new WeakSet<Node>();
  const storageKey = `ee-agent-request:${container.getAttribute('data-agent-sessions-url')}`;
  let request: AgentRequest | null = null;
  try {
    const saved = sessionStorage.getItem(storageKey);
    if (saved) request = JSON.parse(saved);
  } catch {
    // Private browsing may disable session storage; polling still works without the hint.
  }

  function clearRequest() {
    request = null;
    document.querySelector('.ee-agent-request-checking')?.remove();
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // Storage is optional.
    }
  }

  function showRequest() {
    if (!request) return;
    if (!isValidRequest(request)) {
      clearRequest();
      return;
    }
    // a retried session card only arrives with the first poll, so this also runs after each poll
    let anchor: Element | null | undefined;
    if (request.findingCards !== undefined) {
      // the run of a finding has no comment to follow, the answer comes at the end of the timeline
      anchor = document.querySelector('#timeline-comments-end')?.previousElementSibling;
    } else if (request.retrySessionID) {
      anchor = document.querySelector(`.ee-agent-session[data-session-id="${CSS.escape(request.retrySessionID)}"]`);
    } else {
      anchor = document.querySelector(`#${CSS.escape(`issuecomment-${request.commentID}`)}`)?.closest('.timeline-item');
    }
    if (!anchor) return;
    const existing = document.querySelector('.ee-agent-request-checking');
    if (existing) {
      if (anchor.nextElementSibling !== existing) anchor.after(existing); // the anchor card may have moved
      return;
    }
    const card = document.createElement('div');
    card.className = 'timeline-item event ee-agent-session ee-agent-request-checking';
    const badge = document.createElement('span');
    badge.className = 'badge';
    badge.textContent = '...';
    const label = document.createElement('span');
    label.className = 'comment-text-line';
    const labelAttribute = request.findingCards !== undefined ? 'data-finding-checking-label' : request.retrySessionID ? 'data-retry-checking-label' : 'data-request-checking-label';
    label.textContent = container!.getAttribute(labelAttribute) || '';
    card.append(badge, label);
    anchor.after(card);
  }

  function saveRequest(saved: AgentRequest) {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(saved));
    } catch {
      // The next page still has its normal poll if storage is unavailable.
    }
  }

  const form = document.querySelector<HTMLFormElement>('#comment-form');
  form?.addEventListener('fetch-action-success', (event: Event) => {
    const editor = getComboMarkdownEditor(form.querySelector('.combo-markdown-editor'));
    const contentField = new FormData(form).get('content');
    const content = editor?.value() ?? (typeof contentField === 'string' ? contentField : '');
    if (!/(^|[^\w@])@[a-z\d][\w.-]*/i.test(content)) return;
    const redirect = (event as CustomEvent).detail?.redirect;
    if (typeof redirect !== 'string') return;
    const target = new URL(redirect, window.location.href);
    const match = /^#issuecomment-(\d+)$/.exec(target.hash);
    if (!match || target.origin !== window.location.origin) return;
    saveRequest({commentID: match[1], expires: Date.now() + requestCheckDurationMs});
  });

  // Asking an agent on the page of a finding only queues the task and reloads the page, the session
  // is created later by the worker. Remember how many run cards the page had, so the reloaded page
  // polls rapidly until a new one shows up.
  document.addEventListener('submit', (event: Event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.ee-finding-agent-request')) return;
    saveRequest({findingCards: document.querySelectorAll('.ee-agent-session[data-session-id]').length, expires: Date.now() + requestCheckDurationMs});
  }, {capture: true});

  // A retry only queues the task and reloads the page, the new session row is created later by the
  // worker. Remember the retry so the reloaded page polls rapidly until that session shows up,
  // instead of waiting for the idle poll. fetch-action-success does not bubble, so listen in capture.
  document.addEventListener('fetch-action-success', (event: Event) => {
    const button = event.target instanceof Element ? event.target.closest('.ee-agent-session-retry') : null;
    const card = button?.closest<HTMLElement>('.ee-agent-session[data-session-id]');
    if (!card) return;
    saveRequest({
      retrySessionID: card.getAttribute('data-session-id')!,
      agentUser: card.getAttribute('data-agent-user') || '',
      retryCreated: Number(card.getAttribute('data-session-created') || '0'), // server time, the browser clock may be off
      expires: Date.now() + requestCheckDurationMs,
    });
  }, {capture: true});

  showRequest();

  async function poll() {
    // browsers throttle background tabs anyway, skipping hidden tabs keeps the server quiet
    if (document.visibilityState === 'visible') {
      try {
        hasLoaded = await loadAgentSessions(container!, refreshedSessions, addedEntries, !hasLoaded) || hasLoaded;
      } catch {
        // keep polling, a transient network error must not stop the live updates
      }
    }
    if (request && (isRequestAnswered(request) || Date.now() >= request.expires)) {
      clearRequest();
    }
    showRequest();
    showAwaitingStart();
    if (!document.contains(container)) return;
    scheduleNextPoll();
  }

  // A just created issue assigned to an agent has no session yet, the worker queue creates it a
  // moment later. The server says so, and the same "waiting" card as for a mention is shown meanwhile.
  function isAwaitingStart(): boolean {
    return container!.getAttribute('data-agent-awaiting-start') === 'true';
  }

  function showAwaitingStart() {
    const existing = document.querySelector('.ee-agent-awaiting-start');
    if (!isAwaitingStart()) {
      existing?.remove();
      return;
    }
    if (existing) return;
    const anchor = document.querySelector('#timeline-comments-end')?.previousElementSibling;
    if (!anchor) return;
    const card = document.createElement('div');
    card.className = 'timeline-item event ee-agent-session ee-agent-awaiting-start';
    const badge = document.createElement('span');
    badge.className = 'badge';
    badge.textContent = '...';
    const label = document.createElement('span');
    label.className = 'comment-text-line';
    const isMention = container!.getAttribute('data-agent-awaiting-mention') === 'true';
    label.textContent = container!.getAttribute(isMention ? 'data-create-mention-checking-label' : 'data-assign-checking-label') || '';
    card.append(badge, label);
    anchor.after(card);
  }

  function scheduleNextPoll() {
    if (pollTimer) clearTimeout(pollTimer);
    const isActive = container!.getAttribute('data-agent-sessions-active') === 'true';
    const delay = request || isAwaitingStart() ? rapidPollIntervalMs : isActive ? activePollIntervalMs : rapidPolls > 0 ? rapidPollIntervalMs : idlePollIntervalMs;
    if (rapidPolls > 0) rapidPolls--;
    pollTimer = setTimeout(poll, delay);
  }

  function kickPoll() {
    rapidPolls = 5;
    if (pollTimer) clearTimeout(pollTimer);
    poll();
  }

  // issue sidebar updates are applied in place, so the initial page init is not called again.
  // Watch the timeline for the assignment/review-request event and refresh the session card immediately.
  const timelineEnd = document.querySelector<HTMLElement>('#timeline-comments-end');
  const timelineList = timelineEnd?.parentElement;
  if (timelineList) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element && node.classList.contains('timeline-item') && !node.classList.contains('ee-agent-session') && !addedEntries.has(node)) {
            kickPoll();
            return;
          }
        }
      }
    });
    observer.observe(timelineList, {childList: true});
  }

  poll();
}
